import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import UploadWizard from "./_35_upload_wizard.js";
import Carousel from "./_52_climb.js";

const COMMON_TIMEZONES = [
    "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
    "America/Toronto", "Europe/London", "Europe/Paris", "Asia/Tokyo", "Australia/Sydney"
];

// Helper function to get timezone abbreviation
function getTimezoneAbbr(timezone) {
    const now = new Date();
    const tzFormatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        timeZoneName: "short"
    });
    const parts = tzFormatter.formatToParts(now);
    const tzPart = parts.find(p => p.type === "timeZoneName");
    return tzPart ? tzPart.value : timezone.split("/")[1] || timezone;
}

function UploadProgressBar({ progress }) {
    return (
        <div
            style={{
                width: "100%",
                height: 40,
                background: "#eee",
                borderRadius: 20,
                overflow: "hidden",
                margin: "16px 0",
            }}
        >
            <div
                style={{
                    width: `${progress}%`,
                    height: "100%",
                    background: "linear-gradient(90deg, #0a2342 0%, #4caf50 100%)",
                    transition: "width 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: 18,
                    paddingLeft: 16,
                }}
            >
                {progress > 5 ? `${progress}%` : ""}
            </div>
        </div>
    );
}

// Toggle Switch Component (from YTD design)
function ToggleSwitch({ checked, onChange, label, description, isOrange = false }) {
    return (
        <div style={{ marginBottom: 16 }}>
            <label style={{
                display: "inline-flex",
                alignItems: "center",
                cursor: "pointer",
                width: "100%"
            }}>
                <span style={{ marginRight: 12, fontWeight: 600, minWidth: label ? 200 : 0 }}>{label}</span>
                <span style={{
                    position: "relative",
                    display: "inline-block",
                    width: 44,
                    height: 24,
                    marginRight: 12
                }}>
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => onChange(e.target.checked)}
                        style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                        position: "absolute",
                        cursor: "pointer",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: checked ? (isOrange ? "#ff9800" : "#1976d2") : "#888",
                        borderRadius: 24,
                        transition: "background 0.2s"
                    }} />
                    <span style={{
                        position: "absolute",
                        left: checked ? 22 : 2,
                        top: 2,
                        width: 20,
                        height: 20,
                        background: "#fff",
                        borderRadius: "50%",
                        transition: "left 0.2s"
                    }} />
                </span>
            </label>
            {description && (
                <div style={{ color: "#aaa", fontSize: 11, marginTop: 4, marginLeft: label ? 212 : 20 }}>
                    {description}
                </div>
            )}
        </div>
    );
}

// Helper: Validate CSV file client-side
function validateCsvFile(file, setBulkError) {
    // Check file extension
    if (!file.name.toLowerCase().endsWith(".csv")) {
        setBulkError(`File "${file.name}" is not a .csv file.`);
        return false;
    }
    // Check file size (Revised to 2MB but warns at 1MB)
    if (file.size > 2 * 1024 * 1024) {
        setBulkError(`File "${file.name}" exceeds 2MB size limit.`);
        return false;
    }
    if (file.size > 1 * 1024 * 1024) {
        setBulkError(`Warning: File "${file.name}" exceeds 1MB. Failed uploads could happen with larger files. Consider using a smaller date range.`);
        // Note: Returning true here allows the user to proceed despite the warning message
    }
    // Use FileReader to check first line
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = function (e) {
            let text = e.target.result;
            // Remove BOM if present
            if (text.charCodeAt(0) === 0xFEFF) {
                text = text.slice(1);
            }
            // Get first line robustly
            const firstLine = (text.split(/\r?\n/)[0] || "").trim();

            // Validate that it's a thinkorswim Account Statement file
            if (!firstLine.includes("Account Statement")) {
                setBulkError(
                    `File "${file.name}" does not appear to be a valid thinkorswim Account Statement file.`
                );
                resolve(false);
                return;
            }
            resolve(true);
        };
        reader.onerror = function () {
            setBulkError(`Could not read file "${file.name}".`);
            resolve(false);
        };
        // Read only the first 2KB for efficiency
        const blob = file.slice(0, 2048);
        reader.readAsText(blob);
    });
}

const MAX_HISTORY_DISPLAY = 10;

const TradeUpload = (props) => {
    const screen = props.screen;

    const [userKey, setUserKey] = useState("");
    const [showUserPrefTzDropdown, setShowUserPrefTzDropdown] = useState(false);
    const [userPrefTimezone, setUserPrefTimezone] = useState("America/New_York");
    const userPrefTzDropdownRef = useRef(null);
    const userPrefTzDropdownContainerRef = useRef(null);
    const [swapTimezone, setSwapTimezone] = useState(false);
    const [toTimezone, setToTimezone] = useState("America/New_York");
    const [files, setFiles] = useState([]);
    const [bulkProgress, setBulkProgress] = useState(0);
    const [bulkProcessing, setBulkProcessing] = useState(false);
    const [bulkResult, setBulkResult] = useState("");
    const [bulkError, setBulkError] = useState("");
    const [tradeTag, setTradeTag] = useState("");
    const [tagError, setTagError] = useState("");
    const [localTimezone, setLocalTimezone] = useState("");
    const [convertToEST, setConvertToEST] = useState(true);
    const [privacyEnabled, setPrivacyEnabled] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [deleteResult, setDeleteResult] = useState("");
    const [showFileHistory, setShowFileHistory] = useState(false);
    const [fileHistory, setFileHistory] = useState([]);
    const [fileHistoryLoading, setFileHistoryLoading] = useState(false);
    const [fileHistoryError, setFileHistoryError] = useState("");
    const fileInputRef = useRef(null);
    const [showWizard, setShowWizard] = useState(false);
    const [showAllHistory, setShowAllHistory] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);

    // Detect device timezone once on mount
    useEffect(() => {
        if (typeof window !== "undefined") {
            const detected = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
            setLocalTimezone(detected);
        }
    }, []);

    // Show wizard on mount if sticknav is disabled
    useEffect(() => {
        if (typeof window !== "undefined") {
            const sticknavDisabled = !props.sticknav;
            const wizardKey = `wizard_shown`;
            const alreadyShown = window.sessionStorage.getItem(wizardKey);

            if (sticknavDisabled && !alreadyShown) {
                setShowWizard(true);
                window.sessionStorage.setItem(wizardKey, "1");
            }
        }
    }, [props.sticknav]);

    // Fetch file history when section is expanded
    useEffect(() => {
        if (showFileHistory) {
            setFileHistoryLoading(true);
            setFileHistoryError("");
            axios
                .get(`${process.env.REACT_APP_API_URL}/api/v2/upload_history`, { withCredentials: true })
                .then((res) => {
                    setFileHistory(res.data.uploads || []);
                    setFileHistoryLoading(false);
                })
                .catch((err) => {
                    setFileHistoryError("Failed to load file history.");
                    setFileHistoryLoading(false);
                });
        }
    }, [showFileHistory]);

    // Handle folder selection
    const handleFolderSelect = async (e) => {
        let fileList = Array.from(e.target.files)
            .filter(f => f.name.toLowerCase().endsWith(".csv"))
            .sort((a, b) => b.name.localeCompare(a.name)).reverse();

        setBulkProgress(0);
        setBulkResult("");
        setBulkError("");
        setBulkProcessing(false);

        if (fileList.length === 0) {
            setBulkError("No .csv files found in the selected folder.");
            setFiles([]);
            return;
        }

        // Validate all files in parallel
        for (const file of fileList) {
            const valid = await validateCsvFile(file, setBulkError);
            if (!valid) {
                setFiles([]);
                return;
            }
        }
        setFiles(fileList);
    };

    // Handle single file selection
    const handleFileSelect = async (e) => {
        let fileList = Array.from(e.target.files).filter(f => f.name.toLowerCase().endsWith(".csv"));
        setBulkProgress(0);
        setBulkResult("");
        setBulkError("");
        setBulkProcessing(false);

        if (fileList.length === 0) {
            setBulkError("Please select a .csv file.");
            setFiles([]);
            return;
        }
        // Validate the file
        const valid = await validateCsvFile(fileList[0], setBulkError);
        if (!valid) {
            setFiles([]);
            return;
        }
        setFiles(fileList);
    };

    // ═══════════════════════════════════════════════════════════════════
    // UNIFIED BULK SUBMIT - Sends ALL files in single request
    // ═══════════════════════════════════════════════════════════════════
    const handleBulkSubmit = async () => {
        setBulkProcessing(true);
        setBulkProgress(10);
        setBulkResult("");
        setBulkError("");

        const formData = new FormData();
        for (const file of files) {
            formData.append("files", file);
        }

        formData.append("trade_tag", tradeTag);
        formData.append("brokerage", "thinkorswim");
        formData.append("device_timezone", localTimezone || "unknown");
        formData.append("userpref_timezone", userPrefTimezone);
        formData.append("convertToEST", convertToEST ? "true" : "false");
        formData.append("privacy_enabled", privacyEnabled ? "1" : "0");

        // Inject user key if privacy enabled
        if (privacyEnabled && userKey) {
            formData.append("user_key", userKey);
        }

        formData.append("screen", "trade_upload");
        let file_timezone = convertToEST ? userPrefTimezone : (localTimezone || "unknown");
        formData.append("file_timezone", file_timezone);

        try {
            setBulkProgress(30);
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/v2/trade_upload`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                    onUploadProgress: (progressEvent) => {
                        const uploadPercent = Math.round((progressEvent.loaded / progressEvent.total) * 40);
                        setBulkProgress(30 + uploadPercent);
                    }
                }
            );
            setBulkProgress(100);
            if (res.data && res.data.results) {
                setBulkResult("Upload completed successfully.");
                setFiles([]); // Clear files on success
                setUserKey(""); // Clear key after successful upload
            }
        } catch (err) {
            setBulkError(err.response?.data?.error || "Upload failed.");
        }
        setBulkProcessing(false);
    };

    const handleTagChange = (e) => {
        let value = e.target.value;
        if (value.length > 25) value = value.slice(0, 25);
        if (/^[a-zA-Z0-9]*$/.test(value)) {
            setTradeTag(value);
            setTagError("");
        } else {
            setTagError("Trade Tag must be alphanumeric (letters and numbers only, max 25 characters).");
        }
    };

    const handleTimezoneChange = (e) => {
        setLocalTimezone(e.target.value);
    };

    const handleConvertToESTChange = (checked) => {
        setConvertToEST(checked);
    };

    // Handler to delete all records
    const handleDeleteAllRecords = async () => {
        if (window.confirm("Are you sure you want to delete all records for this user?")) {
            try {
                const res = await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/v2/delete_all_records`,
                    { brokerage: "thinkorswim" },
                    { withCredentials: true }
                );

                // Check if deletion was successful
                if (res.data?.success) {
                    setDeleteResult("Successfully deleted.");
                } else {
                    setDeleteResult("Failed to delete.");
                }

                setTimeout(() => setDeleteResult(""), 4000);
            } catch (err) {
                setDeleteResult("Failed to delete.");
                setTimeout(() => setDeleteResult(""), 4000);
            }
        }
    };

    // Helper to format date/time in MM/DD/YY h:mm AM/PM
    function formatUploadTime(dateString) {
        if (!dateString) return "";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        const yy = String(date.getFullYear()).slice(-2);
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        hours = hours ? hours : 12;
        return `${mm}/${dd}/${yy} ${hours}:${minutes} ${ampm}`;
    }

    // Handler to receive updates from UploadWizard
    const handleWizardTimezoneChange = (wizardTimezone, wizardConvertToEST) => {
        setLocalTimezone(wizardTimezone);
        setConvertToEST(wizardConvertToEST);
    };

    // Handler to open the wizard and hide modal
    const handleShowWizard = (e) => {
        e.preventDefault();
        setShowSettingsModal(false);
        setShowWizard(true);
    };

    // Handler to close the wizard
    const handleWizardClose = () => {
        setShowWizard(false);
    };

    // Label for the checkbox
    const convertLabel = (
        <>
            Convert to&nbsp;
            <span
                style={{ color: "#1976d2", textDecoration: "underline", cursor: "pointer" }}
                onMouseDown={e => {
                    e.preventDefault();
                    setShowUserPrefTzDropdown(true);
                }}
                tabIndex={0}
                role="button"
                aria-label="Change timezone"
            >
                {userPrefTimezone}
            </span>
        </>
    );

    const handleSeeMoreHistory = () => {
        setShowAllHistory(true);
    };

    const handleSeeLessHistory = () => {
        setShowAllHistory(false);
    };

    // Render settings modal
    const renderSettingsModal = () => {
        if (!showSettingsModal) return null;
        return (
            <>
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(24,26,37,0.35)",
                        backdropFilter: "blur(2px)",
                        zIndex: 9998
                    }}
                    onClick={() => setShowSettingsModal(false)}
                />
                <div style={{
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 9999,
                    minWidth: 520,
                    minHeight: 400,
                    background: "#181A25",
                    borderRadius: 12,
                    boxShadow: "0 2px 8px rgba(0,255,255,0.08)",
                    padding: 24,
                    maxHeight: "90vh",
                    overflowY: "auto"
                }}>
                    <h3 style={{ marginTop: 0, marginBottom: 20, color: "#fff" }}>Upload Settings</h3>

                    {/* Local Timezone dropdown */}
                    <label htmlFor="from-tz-select" style={{ display: "block", marginBottom: 12, color: "#ccc", fontWeight: 600 }}>
                        From timezone:
                    </label>
                    <select
                        id="from-tz-select"
                        value={localTimezone}
                        onChange={handleTimezoneChange}
                        style={{
                            background: "#222",
                            color: "#fff",
                            border: "1px solid #444",
                            borderRadius: 4,
                            padding: 8,
                            fontSize: 15,
                            width: "100%",
                            marginBottom: 20,
                            display: "block",
                            boxSizing: "border-box"
                        }}
                    >
                        <option value="">(auto-detect)</option>
                        {COMMON_TIMEZONES.map((tz) => (
                            <option key={tz} value={tz}>{tz}</option>
                        ))}
                    </select>

                    {/* Convert to timezone toggle */}
                    <ToggleSwitch
                        checked={convertToEST}
                        onChange={handleConvertToESTChange}
                        label="Convert to timezone"
                        description={`Checked = Convert to ${userPrefTimezone}. Uncheck = Keep original timezone.`}
                    />

                    {/* User Preference Timezone Selector */}
                    {convertToEST && (
                        <>
                            <label htmlFor="user-pref-tz-select" style={{ display: "block", marginBottom: 12, color: "#ccc", fontWeight: 600 }}>
                                Target timezone:
                            </label>
                            <select
                                id="user-pref-tz-select"
                                ref={userPrefTzDropdownRef}
                                value={userPrefTimezone}
                                onChange={e => setUserPrefTimezone(e.target.value)}
                                style={{
                                    background: "#222",
                                    color: "#fff",
                                    border: "1px solid #444",
                                    borderRadius: 4,
                                    padding: 8,
                                    fontSize: 15,
                                    width: "100%",
                                    marginBottom: 20,
                                    display: "block",
                                    boxSizing: "border-box"
                                }}
                            >
                                {COMMON_TIMEZONES.map((tz) => (
                                    <option key={tz} value={tz}>{tz}</option>
                                ))}
                            </select>
                        </>
                    )}

                    {/* Trade Tag field */}
                    <label htmlFor="trade-tag-input" style={{ display: "block", marginBottom: 12, color: "#ccc", fontWeight: 600 }}>
                        Account trade tag (optional):
                    </label>
                    <input
                        id="trade-tag-input"
                        type="text"
                        placeholder="e.g., Main, Demo, etc."
                        value={tradeTag}
                        onChange={handleTagChange}
                        maxLength={25}
                        style={{
                            background: "#222",
                            color: "#fff",
                            border: "1px solid #444",
                            borderRadius: 4,
                            padding: 8,
                            fontSize: 15,
                            width: "100%",
                            marginBottom: tagError ? 4 : 20,
                            boxSizing: "border-box"
                        }}
                    />
                    {tagError && (
                        <div style={{ color: "#ff6b6b", marginBottom: 20, fontSize: 12 }}>{tagError}</div>
                    )}

                    {/* Show instructions wizard link */}
                    <div style={{ borderTop: "1px solid #333", paddingTop: 16, paddingBottom: 16 }}>
                        <a
                            href="#"
                            style={{
                                color: "#1976d2",
                                textDecoration: "underline",
                                fontSize: 15,
                                cursor: "pointer"
                            }}
                            onClick={handleShowWizard}
                        >
                            📖 Show instructions wizard
                        </a>
                    </div>

                    {/* Delete all records link */}
                    <div style={{ borderTop: "1px solid #333", paddingTop: 16 }}>
                        <a
                            href="#"
                            id="delete-records-link"
                            style={{
                                color: "#ff6b6b",
                                textDecoration: "underline",
                                fontSize: 15,
                                cursor: "pointer"
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                                handleDeleteAllRecords();
                            }}
                        >
                            🗑️ Delete all records
                        </a>
                        {deleteResult && (
                            <div style={{ marginTop: 8, fontWeight: "bold", color: "#4caf50", fontSize: 13 }}>
                                {deleteResult}
                            </div>
                        )}
                    </div>

                    {/* Close button */}
                    <button
                        onClick={() => setShowSettingsModal(false)}
                        style={{
                            position: "absolute",
                            top: 12,
                            right: 18,
                            background: "none",
                            border: "none",
                            color: "#fff",
                            fontSize: 22,
                            cursor: "pointer"
                        }}
                        aria-label="Close settings"
                    >
                        ✕
                    </button>
                </div>
            </>
        );
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFiles = Array.from(e.dataTransfer.files).filter(f => f.name.toLowerCase().endsWith(".csv"));

        if (droppedFiles.length === 0) {
            setBulkError("Please drop .csv files.");
            return;
        }

        setBulkProgress(0);
        setBulkResult("");
        setBulkError("");

        const validFiles = [];
        for (const file of droppedFiles) {
            const valid = await validateCsvFile(file, setBulkError);
            if (valid) validFiles.push(file);
        }

        if (validFiles.length > 0) {
            setFiles(prev => [...prev, ...validFiles]);
        }
    };

    return (
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", gap: 32, alignItems: "flex-start", minHeight: 700 }}>
            {/* Left side: carousel */}
            <div style={{ flex: "0 0 420px", textAlign: "center", position: "relative", minHeight: 500, paddingBottom: 32 }}>
                <div style={{ marginTop: 32 }}>
                    <Carousel interval={112000} transitionDuration={399300} />
                </div>
            </div>

            {/* Right side: bulk upload functionality */}
            <div style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                minHeight: 500
            }}>
                <div>
                    {/* Header with gear icon */}
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <h3 style={{ margin: 0 }}>📤 Upload thinkorswim files</h3>
                        <button
                            onClick={() => setShowSettingsModal(true)}
                            title="Upload settings"
                            id="upload-settings-button"
                            style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: "2px 4px",
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 18,
                                transition: "opacity 0.2s ease"
                            }}
                            aria-label="Upload settings"
                            tabIndex={0}
                        >
                            ⚙️
                        </button>
                    </div>

                    {/* Dotted Drag & Drop Area */}
                    <div
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        style={{
                            marginTop: 20,
                            marginBottom: 20,
                            padding: files.length > 0 ? "20px" : "40px 20px",
                            border: `2px dashed ${isDragging ? "#1976d2" : "#444"}`,
                            borderRadius: 12,
                            textAlign: "center",
                            cursor: "pointer",
                            background: isDragging ? "rgba(25, 118, 210, 0.05)" : "transparent",
                            transition: "all 0.2s ease",
                            minHeight: 140,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        <input
                            type="file"
                            multiple
                            accept=".csv"
                            onChange={handleFileSelect}
                            ref={fileInputRef}
                            style={{ display: "none" }}
                        />

                        {files.length === 0 ? (
                            <>
                                <img
                                    src="/images/csv-icon.png"
                                    alt="CSV Icon"
                                    style={{ width: 48, height: 48, marginBottom: 12, opacity: 0.8 }}
                                />
                                <div style={{ color: "#fff", fontSize: 16, fontWeight: 500 }}>
                                    Drag and drop CSV files here
                                </div>
                                <div style={{ color: "#888", fontSize: 13, marginTop: 8 }}>
                                    or click to browse from your computer
                                </div>
                            </>
                        ) : (
                            <div style={{ width: "100%", textAlign: "left" }}>
                                <div style={{ color: "#fff", fontSize: 14, fontWeight: 600, marginBottom: 12, borderBottom: "1px solid #333", paddingBottom: 8, display: "flex", justifyContent: "space-between" }}>
                                    <span>Selected ({files.length} file{files.length > 1 ? "s" : ""}) {getTimezoneAbbr(convertToEST ? userPrefTimezone : localTimezone)}</span>
                                    <span style={{ color: "#1976d2", fontSize: 12 }}>+ Add more</span>
                                </div>
                                <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none", maxHeight: 150, overflowY: "auto" }}>
                                    {files.map((f, i) => (
                                        <li key={i} style={{ color: "#aaa", fontSize: 13, marginBottom: 4, display: "flex", alignItems: "center" }}>
                                            <img src="/images/csv-icon.png" stroke="currentColor" style={{ width: 14, height: 14, marginRight: 8, opacity: 0.6 }} />
                                            {f.name}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Privacy toggle - left aligned, no label */}
                    <div style={{ marginBottom: 12 }}>
                        <ToggleSwitch
                            checked={privacyEnabled}
                            onChange={setPrivacyEnabled}
                            label=""
                            description="Enable data privacy protections for this upload"
                        />
                    </div>

                    {/* Privacy key field - only shown when privacy enabled */}
                    {privacyEnabled && (
                        <div style={{ marginBottom: 20 }}>
                            <label htmlFor="privacy-key-input" style={{ display: "block", marginBottom: 8, color: "#ccc", fontWeight: 600, fontSize: 14 }}>
                                🔒 Privacy Key:
                                <Tooltip
                                    title="Please note this key, files will need to be deleted and uploaded again if you use the wrong key"
                                    placement="right"
                                    arrow
                                >
                                    <span
                                        style={{
                                            display: "inline-block",
                                            marginLeft: 6,
                                            color: "#1976d2",
                                            verticalAlign: "middle",
                                            cursor: "pointer",
                                            fontSize: 16,
                                            lineHeight: 1
                                        }}
                                        tabIndex={0}
                                        aria-label="Privacy key info"
                                    >
                                        &#9432;
                                    </span>
                                </Tooltip>
                            </label>
                            <input
                                id="privacy-key-input"
                                type="password"
                                placeholder="Enter encryption key (min 4 characters)..."
                                value={userKey}
                                onChange={(e) => setUserKey(e.target.value)}
                                style={{
                                    background: "#222",
                                    color: "#fff",
                                    border: `1px solid ${userKey.length < 4 && userKey.length > 0 ? "#ff6b6b" : "#444"}`,
                                    borderRadius: 4,
                                    padding: 10,
                                    fontSize: 14,
                                    width: "100%",
                                    boxSizing: "border-box"
                                }}
                            />
                            {userKey.length > 0 && userKey.length < 4 && (
                                <div style={{ color: "#ff6b6b", fontSize: 12, marginTop: 4 }}>
                                    Key must be at least 4 characters
                                </div>
                            )}
                            {userKey.length >= 4 && (
                                <div style={{ color: "#4caf50", fontSize: 12, marginTop: 4 }}>
                                    ✓ Key ready
                                </div>
                            )}
                        </div>
                    )}

                    {/* Upload button */}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleBulkSubmit}
                        disabled={bulkProcessing || files.length === 0 || (privacyEnabled && userKey.length < 4)}
                        style={{ marginTop: 8 }}
                    >
                        {bulkProcessing ? <CircularProgress size={22} color="inherit" /> : "Upload"}
                    </Button>

                    {/* Progress bar */}
                    {bulkProcessing && <UploadProgressBar progress={bulkProgress} />}

                    {/* Results */}
                    {bulkResult && (
                        <Alert severity="success" style={{ marginTop: 16, whiteSpace: "pre-wrap" }}>
                            {bulkResult}
                        </Alert>
                    )}

                    {/* Errors */}
                    {bulkError && (
                        <Alert severity="error" style={{ marginTop: 16, whiteSpace: "pre-wrap" }}>
                            {bulkError}
                        </Alert>
                    )}

                    {/* File history toggle */}
                    <div style={{ marginTop: 24 }}>
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                setShowFileHistory(!showFileHistory);
                            }}
                            style={{ color: "#1976d2", fontSize: 14 }}
                        >
                            {showFileHistory ? "▼ Hide upload history" : "▶ Show upload history"}
                        </a>
                    </div>

                    {/* File history list */}
                    <Collapse in={showFileHistory}>
                        <div style={{ marginTop: 12, padding: 12, background: "#1a1a2e", borderRadius: 8 }}>
                            {fileHistoryLoading && <CircularProgress size={20} />}
                            {fileHistoryError && <div style={{ color: "#ff6b6b" }}>{fileHistoryError}</div>}
                            {!fileHistoryLoading && !fileHistoryError && fileHistory.length === 0 && (
                                <div style={{ color: "#888" }}>No upload history found.</div>
                            )}
                            {!fileHistoryLoading && fileHistory.length > 0 && (
                                <ul style={{ margin: 0, paddingLeft: 18, color: "#ccc", fontSize: 13 }}>
                                    {(showAllHistory ? fileHistory : fileHistory.slice(0, MAX_HISTORY_DISPLAY)).map((item, i) => (
                                        <li key={i} style={{ marginBottom: 8, lineHeight: 1.4 }}>
                                            <div style={{ fontWeight: 500 }}>
                                                {item.filename}
                                                {item.privacy_enabled && (
                                                    <span style={{ marginLeft: 6, color: "#1976d2", fontSize: 12 }} title="Privacy enabled">
                                                        🔒
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: 12, color: "#aaa" }}>
                                                {item.upload_time} • {item.file_timezone}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {fileHistory.length > MAX_HISTORY_DISPLAY && !showAllHistory && (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleSeeMoreHistory();
                                    }}
                                    style={{ color: "#1976d2", fontSize: 12, marginTop: 8, display: "inline-block" }}
                                >
                                    See more...
                                </a>
                            )}
                            {showAllHistory && fileHistory.length > MAX_HISTORY_DISPLAY && (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleSeeLessHistory();
                                    }}
                                    style={{ color: "#1976d2", fontSize: 12, marginTop: 8, display: "inline-block" }}
                                >
                                    See less
                                </a>
                            )}
                        </div>
                    </Collapse>
                </div>
            </div>

            {/* Settings Modal */}
            {renderSettingsModal()}

            {/* Upload Wizard */}
            {showWizard && (
                <UploadWizard
                    forceOpen={showWizard}
                    onClose={handleWizardClose}
                    onTimezoneChange={handleWizardTimezoneChange}
                    deviceTimezone={localTimezone}
                    userPrefTimezone={userPrefTimezone}
                    initialTimezone={localTimezone}
                    initialConvertToEST={convertToEST}
                />
            )}
        </div>
    );
};

export default TradeUpload;